import { type ToneName, tones } from '@shkola/design';
import type { CSSProperties } from 'react';
import { ArrowCounterClockwiseIcon } from '../../design/icons';

/*
 * The small pictures of the home (27.10, refs «Google Labs» cards, «Day in history», «Progress goals»):
 * flat white things on their card's pastel, an accent in the card's own tone — each shows what its card
 * is about (an alarm clock for what is late, a paper plane for what to hand in, a sheet with ticks for a
 * test…). Decoration only: hidden from screen readers. The greeting gets the school itself, with its
 * clock telling the time and the sky of the time of day.
 */

export interface ArtProps {
  tone: ToneName;
  className?: string;
  style?: CSSProperties;
  /** where it stands inside another picture (a pedestal of «Скло») */
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

const WHITE = '#FFFFFF';

function Art({
  className,
  style,
  x,
  y,
  width,
  height,
  children,
}: Omit<ArtProps, 'tone'> & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 120 120"
      aria-hidden
      focusable="false"
      className={`pointer-events-none select-none ${className ?? ''}`}
      style={style}
      x={x}
      y={y}
      width={width}
      height={height}
    >
      {children}
    </svg>
  );
}

/** A four-point sparkle centred at (x, y). */
export const sparkle = (x: number, y: number, s: number) =>
  `M${x} ${y - s}Q${x} ${y} ${x + s} ${y}Q${x} ${y} ${x} ${y + s}Q${x} ${y} ${x - s} ${y}Q${x} ${y} ${x} ${y - s}Z`;

/** What is late: an alarm clock ringing. */
export function AlarmArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  const ticks = Array.from({ length: 12 }, (_, i) => {
    const a = (i * Math.PI) / 6;
    return { x: 60 + Math.sin(a) * 24, y: 66 - Math.cos(a) * 24, major: i % 3 === 0 };
  });
  return (
    <Art {...rest}>
      <path
        d="M104 48q7 14 0 28M112 40q11 22 0 44"
        stroke={WHITE}
        strokeWidth="4.5"
        strokeLinecap="round"
        fill="none"
      />
      <g transform="rotate(-12 60 66)">
        <path d="M38 96 30 109M82 96l8 13" stroke={WHITE} strokeWidth="7" strokeLinecap="round" />
        <circle cx="33" cy="32" r="13" fill={t.icon} />
        <circle cx="87" cy="32" r="13" fill={t.icon} />
        <path d="M60 28v-6" stroke={t.ink} strokeWidth="4" strokeLinecap="round" />
        <circle cx="60" cy="66" r="38" fill={WHITE} />
        <circle cx="60" cy="66" r="31" fill="none" stroke={t.soft} strokeWidth="3" />
        {ticks.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={p.major ? 2.6 : 1.8}
            fill={p.major ? t.icon : t.from}
          />
        ))}
        <path
          d="M60 66V47M60 66l15 8"
          stroke={t.ink}
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="60" cy="66" r="4.5" fill={t.vivid} />
      </g>
    </Art>
  );
}

/** What to hand in: a paper plane flying off. */
export function PlaneArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <path
        d="M6 108C26 106 30 80 52 74"
        stroke={WHITE}
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeDasharray="0.1 10"
        fill="none"
      />
      <path d="M114 14 30 50l30 14Z" fill={WHITE} />
      <path d="M114 14 60 64l18 32Z" fill={t.from} />
      <path d="M60 64 66 82 74 72Z" fill={t.icon} fillOpacity="0.55" />
      <path d="M114 14 60 64" stroke={t.icon} strokeOpacity="0.35" strokeWidth="2" />
    </Art>
  );
}

/** A test to prepare for: a sheet with ticks and a pencil. */
export function ExamArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <rect
        x="36"
        y="12"
        width="60"
        height="82"
        rx="10"
        fill={WHITE}
        fillOpacity="0.55"
        transform="rotate(9 66 53)"
      />
      <g transform="rotate(-6 55 62)">
        <rect x="24" y="20" width="62" height="86" rx="10" fill={WHITE} />
        <rect x="34" y="31" width="30" height="6" rx="3" fill={t.from} />
        {[0, 1, 2].map((i) => {
          const y = 52 + i * 17;
          return (
            <g key={i}>
              <circle cx="39" cy={y} r="5.5" fill={i < 2 ? t.icon : t.soft} />
              {i < 2 && (
                <path
                  d={`M36.4 ${y}l2 2.2 3.6-4.2`}
                  stroke={WHITE}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              )}
              <rect x="50" y={y - 3} width={i === 1 ? 22 : 28} height="6" rx="3" fill={t.soft} />
            </g>
          );
        })}
      </g>
      <g transform="rotate(36 96 66)">
        <rect x="90" y="30" width="13" height="58" rx="3.5" fill={t.icon} />
        <rect x="90" y="30" width="13" height="10" rx="3.5" fill={t.ink} />
        <path d="M90 88h13l-6.5 13Z" fill="#FFE6CC" />
        <path d="M94.4 97h4.2l-2.1 4Z" fill={t.ink} />
      </g>
    </Art>
  );
}

/** A work to redo: a sheet with a round arrow. */
export function ReworkArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <g transform="rotate(-7 52 58)">
        <rect x="20" y="14" width="62" height="86" rx="10" fill={WHITE} />
        <rect x="30" y="27" width="34" height="6" rx="3" fill={t.from} />
        <rect x="30" y="42" width="42" height="5" rx="2.5" fill={t.soft} />
        <rect x="30" y="54" width="32" height="5" rx="2.5" fill={t.soft} />
        <rect x="30" y="66" width="38" height="5" rx="2.5" fill={t.soft} />
        <path
          d="M30 82q5-6 10 0t10 0 10 0"
          stroke={t.icon}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      </g>
      <circle cx="88" cy="84" r="23" fill={t.icon} />
      <ArrowCounterClockwiseIcon x={75} y={71} size={26} weight="bold" color={WHITE} />
    </Art>
  );
}

/** An announcement to confirm: a megaphone calling. */
export function MegaphoneArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <path
        d="M101 42q9 18 0 36M110 34q13 26 0 52"
        stroke={WHITE}
        strokeWidth="4.5"
        strokeLinecap="round"
        fill="none"
      />
      <g transform="rotate(-14 58 62)">
        <rect x="20" y="50" width="16" height="26" rx="6" fill={t.icon} />
        <path d="M46 75l7 21q1.5 4 5.5 3l5-1.5q3.5-1 2.5-5L60 77Z" fill={t.icon} />
        <path d="M34 50 84 28q6-2.5 6 4v62q0 6.5-6 4L34 76Z" fill={WHITE} />
        <path d="M58 40v46" stroke={t.soft} strokeWidth="6" />
        <ellipse cx="89" cy="63" rx="6.5" ry="31" fill={t.from} />
      </g>
    </Art>
  );
}

/** Missed lessons to catch up on: a dotted way to a flag. */
export function FlagArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <path
        d="M6 110c22 4 26-24 46-20s22 6 30 2"
        stroke={WHITE}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray="0.1 12"
        fill="none"
      />
      <ellipse cx="84" cy="94" rx="14" ry="5" fill={WHITE} />
      <path d="M84 94V20" stroke={t.ink} strokeWidth="4.5" strokeLinecap="round" />
      <path d="M86 21q14-4 28 4l-6 10 6 10q-14-8-28-4Z" fill={t.icon} />
      <path d={sparkle(30, 34, 8)} fill={WHITE} />
      <path d={sparkle(48, 18, 5)} fill={WHITE} fillOpacity="0.8" />
    </Art>
  );
}

/** Nothing to do: a big tick with sparkles. */
export function DoneArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <circle cx="64" cy="62" r="38" fill={WHITE} />
      <circle cx="64" cy="62" r="27" fill={t.soft} />
      <path
        d="M51 62l9 9 18-19"
        stroke={t.icon}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d={sparkle(16, 28, 9)} fill={WHITE} />
      <path d={sparkle(108, 20, 7)} fill={WHITE} />
      <path d={sparkle(106, 100, 8)} fill={WHITE} fillOpacity="0.85" />
    </Art>
  );
}

/** What to pack: a school backpack. */
export function BackpackArt({ tone, ...rest }: ArtProps) {
  const t = tones[tone];
  return (
    <Art {...rest}>
      <path
        d="M47 32q0-16 13-16t13 16"
        stroke={WHITE}
        strokeWidth="6.5"
        strokeLinecap="round"
        fill="none"
      />
      <rect x="26" y="28" width="68" height="84" rx="24" fill={WHITE} />
      <path d="M26 54q34 14 68 0v-2q0-24-24-24H50q-24 0-24 24Z" fill={t.from} />
      <rect x="40" y="72" width="40" height="30" rx="10" fill={t.soft} />
      <rect x="56" y="66" width="8" height="13" rx="3" fill={t.icon} />
    </Art>
  );
}

// —— the greeting's picture ——

const KYIV_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Kyiv',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** A cloud of three puffs, drifting slowly. */
function Cloud({ x, y, s, delay }: { x: number; y: number; s: number; delay: number }) {
  return (
    <g
      className="motion-drift"
      style={{ '--duration': '16s', animationDelay: `${delay}s` } as CSSProperties}
    >
      <g transform={`translate(${x} ${y}) scale(${s})`} fill={WHITE} fillOpacity="0.92">
        <circle cx="0" cy="0" r="13" />
        <circle cx="17" cy="-8" r="17" />
        <circle cx="35" cy="0" r="12" />
        <rect x="0" y="-2" width="35" height="14" rx="7" />
      </g>
    </g>
  );
}

/**
 * The school under the sky of the time of day: the sun by day, the moon and lit windows in the
 * evening; the clock on its tower tells the time (Kyiv), the flag of Ukraine over it.
 */
export function SchoolArt({ now, className }: { now: number; className?: string }) {
  const [h = 0, m = 0] = KYIV_TIME.format(new Date(now)).split(':').map(Number);
  const day = h >= 7 && h < 18;
  const hour = ((h % 12) + m / 60) * 30;
  const minute = m * 6;
  const roof = '#F0877F';
  const navy = '#2B3354';
  const window = day ? '#CFE2FA' : '#FFE29A';
  const columns = [134, 156, 178, 224, 246, 268];
  return (
    <svg
      viewBox="0 0 400 240"
      aria-hidden
      focusable="false"
      className={`pointer-events-none select-none ${className ?? ''}`}
    >
      <defs>
        <mask id="school-moon">
          <rect width="400" height="240" fill={WHITE} />
          <circle cx="332" cy="52" r="22" fill="#000" />
        </mask>
      </defs>
      {day ? (
        <g>
          <circle cx="322" cy="64" r="50" fill={WHITE} fillOpacity="0.35" />
          <circle cx="322" cy="64" r="28" fill="#FFD978" />
        </g>
      ) : (
        <g>
          <circle cx="320" cy="62" r="26" fill="#FFF3CF" mask="url(#school-moon)" />
          <path d={sparkle(262, 40, 6)} fill={WHITE} />
          <path d={sparkle(372, 108, 5)} fill={WHITE} />
          <path d={sparkle(286, 104, 4)} fill={WHITE} fillOpacity="0.8" />
        </g>
      )}
      <Cloud x={46} y={70} s={1.15} delay={0} />
      <Cloud x={238} y={118} s={0.8} delay={-6} />

      <path d="M0 240C70 214 140 200 210 200S350 214 400 240Z" fill={WHITE} fillOpacity="0.55" />

      <g>
        <rect x="62" y="172" width="8" height="40" rx="3" fill="#B98B62" />
        <circle cx="66" cy="160" r="26" fill="#9FDBB8" />
        <circle cx="58" cy="152" r="12" fill="#BDEACF" />
        <rect x="336" y="182" width="7" height="30" rx="3" fill="#B98B62" />
        <circle cx="340" cy="174" r="19" fill="#9FDBB8" />
        <circle cx="334" cy="168" r="8" fill="#BDEACF" />
      </g>

      <g>
        <path d="M208 40V12" stroke="#7D879B" strokeWidth="2.5" strokeLinecap="round" />
        <rect x="209" y="12" width="22" height="7" rx="1.5" fill="#3D7BE0" />
        <rect x="209" y="19" width="22" height="7" rx="1.5" fill="#FFD34D" />
        <rect x="120" y="112" width="176" height="104" rx="10" fill={WHITE} />
        <rect x="112" y="104" width="192" height="14" rx="7" fill={roof} />
        <rect x="178" y="70" width="60" height="48" fill={WHITE} />
        <path d="M168 78 208 40l40 38Z" fill={roof} />
        <circle cx="208" cy="96" r="15" fill={WHITE} stroke="#E4E9F2" strokeWidth="3" />
        <path
          d={`M208 96l${(Math.sin((hour * Math.PI) / 180) * 7).toFixed(2)} ${(-Math.cos((hour * Math.PI) / 180) * 7).toFixed(2)}`}
          stroke={navy}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d={`M208 96l${(Math.sin((minute * Math.PI) / 180) * 10.5).toFixed(2)} ${(-Math.cos((minute * Math.PI) / 180) * 10.5).toFixed(2)}`}
          stroke={navy}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle cx="208" cy="96" r="2" fill={roof} />
        {[130, 164].map((y) =>
          columns.map((x) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="15" height="19" rx="4" fill={window} />
          )),
        )}
        <path d="M198 216v-36q0-10 10-10t10 10v36Z" fill={navy} />
        <rect x="188" y="213" width="40" height="5" rx="2.5" fill="#E3E8F1" />
      </g>

      <path d="M40 240C110 222 300 220 372 240Z" fill={WHITE} fillOpacity="0.85" />
      <circle cx="128" cy="220" r="10" fill="#B5E5C8" />
      <circle cx="142" cy="223" r="8" fill="#9FDBB8" />
      <circle cx="286" cy="222" r="9" fill="#9FDBB8" />
    </svg>
  );
}
